import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../utils/axios';
import Swal from 'sweetalert2';
import BottomNav from '../../components/layouts/BottomNav';
import { FaArrowLeft, FaCalendarAlt, FaMapMarkerAlt, FaClock, FaUser, FaEnvelope, FaPhone, FaCheckCircle } from 'react-icons/fa';

const API_BASE = process.env.REACT_APP_BASE_URL || "http://localhost:5000/";


const EventRegistrationPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [event, setEvent] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: ''
  });

  useEffect(() => {
    const fetchEvent = async () => {
      try {
        setIsLoading(true);
        const response = await api.get(`/events/${id}`);
        setEvent(response.data);
      } catch (error) {
        console.error('Error fetching event:', error);
        Swal.fire({
          icon: 'error',
          title: 'Event Not Found',
          text: 'The event you are looking for does not exist.',
          confirmButtonColor: '#1a1a1a'
        }).then(() => {
          navigate('/dashboard');
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchEvent();
  }, [id, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.email.trim() || !formData.phone.trim()) {
      Swal.fire({
        icon: 'error',
        title: 'Missing Information',
        text: 'Please fill in all required fields',
        confirmButtonColor: '#1a1a1a'
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await api.post('/event-registrations', {
        event_id: event.id,
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim()
      });

      Swal.fire({
        icon: 'success',
        title: 'Registration Successful!',
        text: `You have successfully registered for "${event.title}"`,
        confirmButtonColor: '#1a1a1a'
      }).then(() => {
        navigate('/dashboard');
      });
    } catch (error) {
      console.error('Registration error:', error);
      Swal.fire({
        icon: 'error',
        title: 'Registration Failed',
        text: error.response?.data?.message || 'Unable to register. Please try again.',
        confirmButtonColor: '#1a1a1a'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-black border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Loading event details...</p>
        </div>
      </div>
    );
  }

  if (!event) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 event-registration-page">
      {/* Header with gradient background */}
      <div className="bg-gradient-to-r from-black to-gray-800 shadow-lg sticky top-0 z-50">
        <div className="flex items-center px-4 py-4">
          <button
            onClick={() => navigate('/dashboard')}
            className="p-2 -ml-2 mr-3 text-white hover:text-gray-300 transition-colors duration-200 rounded-full hover:bg-white hover:bg-opacity-10"
          >
            <FaArrowLeft size={20} />
          </button>
          <h1 className="text-lg font-semibold text-white">Event Registration</h1>
        </div>
      </div>

      {/* Event Details Section with enhanced design */}
      <div className="bg-white mx-4 mt-4 rounded-2xl shadow-xl overflow-hidden event-details-section transform hover:scale-[1.01] transition-transform duration-300">
        {/* Event Image with overlay */}
        {event.image_path && (
          <div className="relative h-56 overflow-hidden event-image-container">
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent z-10"></div>
            <img
              src={event.image_path.startsWith('http') 
                ? event.image_path 
                : API_BASE + event.image_path.replace(/^\/+/, '')}
              alt={event.title}
              className="w-full h-full object-cover"
            />
            {/* Event title overlay on image */}
            <div className="absolute bottom-0 left-0 right-0 p-6 z-20">
              <h2 className="text-2xl font-bold text-white mb-2 event-title drop-shadow-lg">
                {event.title}
              </h2>
            </div>
          </div>
        )}

        {/* Event Info with enhanced styling */}
        <div className="p-6 event-info-container">
          {!event.image_path && (
            <h2 className="text-2xl font-bold text-gray-900 mb-4 event-title">
              {event.title}
            </h2>
          )}
          
          {event.description && (
            <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-4 mb-6 border border-gray-200">
              <p className="text-gray-700 leading-relaxed">
                {event.description}
              </p>
            </div>
          )}

          {/* Event Details with enhanced cards */}
          <div className="space-y-4">
            <div className="flex items-center p-4 bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl border border-blue-200 transform hover:scale-[1.02] transition-transform duration-200">
              <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full flex items-center justify-center mr-4 shadow-lg">
                <FaCalendarAlt className="text-white" size={18} />
              </div>
              <div>
                <p className="text-sm text-blue-600 font-semibold uppercase tracking-wide">Date</p>
                <p className="text-gray-900 font-bold text-lg">{formatDate(event.date)}</p>
              </div>
            </div>
            
            <div className="flex items-center p-4 bg-gradient-to-r from-green-50 to-green-100 rounded-xl border border-green-200 transform hover:scale-[1.02] transition-transform duration-200">
              <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-r from-green-500 to-green-600 rounded-full flex items-center justify-center mr-4 shadow-lg">
                <FaClock className="text-white" size={18} />
              </div>
              <div>
                <p className="text-sm text-green-600 font-semibold uppercase tracking-wide">Time</p>
                <p className="text-gray-900 font-bold text-lg">{formatTime(event.date)}</p>
              </div>
            </div>
            
            {event.location && (
              <div className="flex items-center p-4 bg-gradient-to-r from-purple-50 to-purple-100 rounded-xl border border-purple-200 transform hover:scale-[1.02] transition-transform duration-200">
                <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-r from-purple-500 to-purple-600 rounded-full flex items-center justify-center mr-4 shadow-lg">
                  <FaMapMarkerAlt className="text-white" size={18} />
                </div>
                <div>
                  <p className="text-sm text-purple-600 font-semibold uppercase tracking-wide">Location</p>
                  <p className="text-gray-900 font-bold text-lg">{event.location}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Registration Form Section with enhanced design */}
      <div className="bg-white mx-4 mt-6 rounded-2xl shadow-xl overflow-hidden transform hover:scale-[1.01] transition-transform duration-300">
        <div className="p-6 registration-form-container">
          {/* Form Header */}
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-gradient-to-r from-black to-gray-700 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
              <FaUser className="text-white" size={28} />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2 form-title">
              Register for this Event
            </h3>
            <p className="text-gray-600">Fill in your details to secure your spot</p>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Full Name with icon */}
            <div className="relative group">
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Full Name *
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <FaUser className="h-5 w-5 text-gray-400 group-focus-within:text-black transition-colors duration-200" />
                </div>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="w-full pl-12 pr-4 py-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-black focus:border-transparent transition-all duration-200 form-input bg-gray-50 focus:bg-white group-hover:border-gray-300"
                  placeholder="Enter your full name"
                  required
                />
              </div>
            </div>

            {/* Email Address with icon */}
            <div className="relative group">
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Email Address *
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <FaEnvelope className="h-5 w-5 text-gray-400 group-focus-within:text-black transition-colors duration-200" />
                </div>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full pl-12 pr-4 py-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-black focus:border-transparent transition-all duration-200 form-input bg-gray-50 focus:bg-white group-hover:border-gray-300"
                  placeholder="Enter your email address"
                  required
                />
              </div>
            </div>

            {/* Phone Number with icon */}
            <div className="relative group">
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Phone Number *
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <FaPhone className="h-5 w-5 text-gray-400 group-focus-within:text-black transition-colors duration-200" />
                </div>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="w-full pl-12 pr-4 py-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-black focus:border-transparent transition-all duration-200 form-input bg-gray-50 focus:bg-white group-hover:border-gray-300"
                  placeholder="Enter your phone number"
                  required
                />
              </div>
            </div>

            {/* Submit Button with enhanced design */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-gradient-to-r from-black to-gray-800 text-white py-5 px-6 rounded-xl font-bold text-lg hover:from-gray-800 hover:to-black disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98] shadow-lg hover:shadow-xl submit-button group"
            >
              {isSubmitting ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-2 border-white border-t-transparent mr-3"></div>
                  Registering...
                </div>
              ) : (
                <div className="flex items-center justify-center">
                  <FaCheckCircle className="mr-2 group-hover:scale-110 transition-transform duration-200" />
                  Register for Event
                </div>
              )}
            </button>
          </form>
        </div>
      </div>

      {/* Bottom Navigation */}
      <BottomNav />
    </div>
  );
};

export default EventRegistrationPage;
